import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Maximize2, Minimize2, ArrowLeft, Bike, ShoppingBag, Clock, Volume2, VolumeX } from 'lucide-react';
import api from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { PrepTimeControl } from '../../components/admin/PrepTimeControl';

const REFRESH_MS = 8000;
const AMBER_MIN = 8;
const RED_MIN = 12;

const COLUMNS = [
  { key: 'queued', label: 'NEW', accent: '#ED2E2E', next: 'in_kitchen', nextLabel: 'Start' },
  { key: 'in_kitchen', label: 'PREPARING', accent: '#F5A623', next: 'ready', nextLabel: 'Ready' },
  { key: 'ready', label: 'READY', accent: '#22C55E', next: 'completed', nextLabel: 'Handed Over' },
];

function ageMinutes(order) {
  const raw = order.created_at || order.createdAt;
  if (!raw) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(raw).getTime()) / 60000));
}

function ageLabel(mins) {
  if (mins < 60) return `${mins}m`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ${mins % 60}m`;
  return `${Math.floor(mins / 1440)}d`;
}

export default function AdminKitchenTV() {
  const { user, loading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [tick, setTick] = useState(0);
  const [fs, setFs] = useState(false);
  const [sound, setSound] = useState(true);
  const prevIds = useRef(new Set());
  const audioRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get('/admin/kitchen');
      const list = r.data || [];
      const fresh = list.filter((o) => !prevIds.current.has(o.id));
      if (prevIds.current.size > 0 && fresh.length && sound) {
        try { audioRef.current?.play?.().catch(() => {}); } catch (_) {}
      }
      prevIds.current = new Set(list.map((o) => o.id));
      setOrders(list);
    } catch (_) { /* keep last board on screen */ }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    const t = setInterval(load, REFRESH_MS);
    const c = setInterval(() => setTick((n) => n + 1), 30000);
    return () => { clearInterval(t); clearInterval(c); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sound]);

  const toggleFs = async () => {
    try {
      if (!document.fullscreenElement) { await document.documentElement.requestFullscreen(); setFs(true); }
      else { await document.exitFullscreen(); setFs(false); }
    } catch (_) { /* ignore */ }
  };

  const advance = async (o, next) => {
    try {
      await api.post(`/admin/orders/${o.id}/status`, { kitchenStatus: next });
      setOrders((list) => list.map((x) => (x.id === o.id ? { ...x, kitchenStatus: next } : x))
        .filter((x) => x.kitchenStatus !== 'completed'));
      load();
    } catch (_) { /* ignore */ }
  };

  const grouped = useMemo(() => {
    const g = { queued: [], in_kitchen: [], ready: [] };
    orders.forEach((o) => {
      const k = o.kitchenStatus || 'queued';
      if (g[k]) g[k].push(o);
    });
    Object.values(g).forEach((arr) => arr.sort((a, b) => ageMinutes(b) - ageMinutes(a)));
    return g;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, tick]);

  if (loading) return <div className="min-h-screen bg-[#0B0F14] text-white flex items-center justify-center">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-[#0B0F14] text-white select-none" data-testid="kitchen-tv">
      <audio ref={audioRef} src="data:audio/wav;base64,UklGRlYAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQwAAABTAABEAAAAJAAAABAAAA==" preload="auto" />

      <header className="flex items-center justify-between px-6 py-3 border-b border-white/10 bg-black/40">
        <div className="flex items-center gap-4">
          <span className="font-heading font-black text-2xl tracking-tight text-[#ED2E2E]">WRAPPUS</span>
          <span className="uppercase tracking-[0.3em] text-xs text-white/50">Kitchen Display</span>
          <div className="hidden md:block"><PrepTimeControl dark /></div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-white/50 text-sm tabular-nums mr-2">{new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
          <button onClick={() => setSound((s) => !s)} data-testid="tv-sound" className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition">
            {sound ? <Volume2 size={18} /> : <VolumeX size={18} />}
          </button>
          <button onClick={toggleFs} data-testid="tv-fullscreen" className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition">
            {fs ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
          </button>
          <Link to="/admin/kitchen" data-testid="tv-exit" className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition"><ArrowLeft size={18} /></Link>
        </div>
      </header>

      <div className="grid grid-cols-3 gap-3 p-3" style={{ height: 'calc(100vh - 56px)' }}>
        {COLUMNS.map((col) => (
          <section key={col.key} className="flex flex-col min-h-0 rounded-2xl bg-white/[0.03] border border-white/10">
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <div className="flex items-center gap-3">
                <span className="w-3 h-3 rounded-full" style={{ background: col.accent }} />
                <h2 className="font-heading font-black text-2xl tracking-wide">{col.label}</h2>
              </div>
              <span className="font-heading font-black text-3xl tabular-nums" style={{ color: col.accent }} data-testid={`tv-count-${col.key}`}>
                {grouped[col.key].length}
              </span>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-3">
              {grouped[col.key].length === 0 && (
                <div className="text-white/25 text-center pt-10 text-lg">Nothing here</div>
              )}
              {grouped[col.key].map((o) => (
                <TvCard key={o.id} order={o} col={col} onAdvance={advance} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function TvCard({ order, col, onAdvance }) {
  const mins = ageMinutes(order);
  const late = mins >= RED_MIN;
  const warn = !late && mins >= AMBER_MIN;
  const timerColor = late ? '#ED2E2E' : warn ? '#F5A623' : 'rgba(255,255,255,0.45)';
  const isDelivery = order.type === 'delivery';

  return (
    <article
      data-testid={`tv-order-${order.id}`}
      className="rounded-xl bg-[#121821] border-2 p-4"
      style={{ borderColor: late ? '#ED2E2E' : warn ? 'rgba(245,166,35,0.6)' : 'rgba(255,255,255,0.08)' }}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <div className="font-heading font-black text-4xl leading-none tracking-tight">#{order.id.slice(0, 4).toUpperCase()}</div>
          <div className="text-white/60 text-sm mt-1 flex items-center gap-1.5">
            {isDelivery ? <Bike size={14} /> : <ShoppingBag size={14} />}
            {isDelivery ? 'Delivery' : 'Pickup'} · {order.customerName || '—'}
          </div>
        </div>
        <div className="text-right">
          <div className="font-heading font-black text-2xl tabular-nums flex items-center gap-1 justify-end" style={{ color: timerColor }}>
            <Clock size={16} /> {ageLabel(mins)}
          </div>
          {late && <div className="text-[#ED2E2E] text-xs font-black tracking-widest mt-0.5">LATE</div>}
          {order.scheduledFor && (
            <div className="text-[#F5A623] text-xs font-semibold mt-1">
              {new Date(order.scheduledFor).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
            </div>
          )}
        </div>
      </div>

      <ul className="space-y-2 border-t border-white/10 pt-2">
        {(order.items || []).map((it, i) => (
          <li key={i}>
            <div className="text-xl font-bold leading-tight">
              <span className="text-[#ED2E2E]">{it.qty}×</span> {it.name}
            </div>
            {(it.modifiers || []).length > 0 && (
              <div className="text-base text-white/65 pl-7 leading-snug">
                {Object.entries((it.modifiers || []).reduce((acc, m) => {
                  const g = m.groupName || 'Options';
                  acc[g] = acc[g] || [];
                  acc[g].push(m.name);
                  return acc;
                }, {})).map(([g, v]) => (
                  <div key={g}><span className="text-white/35 uppercase text-xs tracking-wider mr-1">{g}</span>{v.join(', ')}</div>
                ))}
              </div>
            )}
          </li>
        ))}
      </ul>

      {order.notes && (
        <div className="mt-2 rounded-lg bg-[#F5A623]/15 border border-[#F5A623]/40 px-3 py-2 text-base text-[#FFD9A0]">
          <b>NOTE:</b> {order.notes}
        </div>
      )}

      <button
        onClick={() => onAdvance(order, col.next)}
        data-testid={`tv-advance-${order.id}`}
        className="mt-3 w-full rounded-xl py-3 font-heading font-black text-lg tracking-wide transition active:scale-[0.98]"
        style={{ background: col.accent, color: col.key === 'in_kitchen' ? '#0B0F14' : '#fff' }}
      >
        {col.nextLabel}
      </button>
    </article>
  );
}
